import * as R from 'ramda';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { datadogRum } from '@datadog/browser-rum';
import {
  GetLatestCommitResponse,
  Role,
  UserAccount,
  UserCompany
} from '@blisspointmedia/bpm-types/dist/Users';
import {
  checkAuth,
  initPeriodicRefresh,
  oauthRedirectInProgress,
  refreshCognitoToken,
  signOutAWS
} from '../utils/auth';
import { decodeJwtEmail, recordAccessDenied } from '../utils/auth/access-denied';
import { getTokens, onSignedOut, resetRefreshBackoff } from '../utils/auth/auth-client';
import { NotSignedInError } from '../utils/auth/auth-errors';
import { captureIntendedDestination, followReturnToAfterBoot } from '../utils/auth/session';
import { reportError } from '../utils/errorReporting';
import { FetchError } from '../utils/fetch-error';
import { awaitJSON, UsersLambdaFetch } from '../utils/fetch-utils';
import { AppDispatch, RootState } from '.';
import { makeReducer } from './utils';

export type { UserCompany };

// Check for a new version of the app every half-hour
const VERSION_CHECK_INTERVAL = 1000 * 60 * 30;
const PAGE_TIMEOUT = 10000; // 10 seconds
const ROOT = 'user';

const SET_USER_INFO = `${ROOT}/SET_USER_INFO`;
const SET_IS_AUTHED = `${ROOT}/SET_IS_AUTHED`;
const SET_AUTH_UNAVAILABLE = `${ROOT}/SET_AUTH_UNAVAILABLE`;
const SET_UP_TO_DATE = `${ROOT}/SET_UP_TO_DATE`;
const SET_UP_TO_DATE_DISMISSED = `${ROOT}/SET_UP_TO_DATE_DISMISSED`;

// The Redux user slice — every field on `UserAccount` (the /me response) plus
// locally owned UI/auth bookkeeping. UserAccount fields are seeded with stub
// defaults in the initial state and overwritten wholesale by setUserInfo once
// /me returns.
interface UserState extends UserAccount {
  userFetched: boolean;
  appUpToDate: boolean;
  appUpToDateDismissed: boolean;
  startTime: number;
  isAuthed?: boolean | null;
  // True while the boot-time session check couldn't get an answer from /auth/refresh or /me (5xx,
  // network) or took longer than PAGE_TIMEOUT. The session is NOT known to be gone: the app shows
  // a retry view instead of reloading or signing out. Cleared by useRetryAuth() and by any
  // definitive answer.
  authUnavailable?: boolean;
}

export const appUpToDateDismissedSelector = (state: RootState) => state.user.appUpToDateDismissed;
export const appUpToDateSelector = (state: RootState) => state.user.appUpToDate;
export const companiesSelector = (state: RootState): UserCompany[] => state.user.companies;
export const emailSelector = (state: RootState) => state.user.email;
export const fullNameSelector = (state: RootState) => state.user.fullName;
export const isAdminSelector = (state: RootState) => state.user.isAdmin;
export const isAuthedSelector = (state: RootState) => state.user.isAuthed;
export const authUnavailableSelector = (state: RootState): boolean =>
  Boolean(state.user.authUnavailable);
export const isInternalSelector = (state: RootState) => state.user.isInternal;
export const isNOCSelector = (state: RootState): boolean => {
  // Falls back to Role.EXTERNAL so a not-yet-loaded session behaves like an external
  // account (no admin/internal/NOC gates open) rather than briefly appearing elevated.
  const role = state.user.companies?.[0]?.role ?? Role.EXTERNAL;
  return role <= Role.NOC;
};
// Falls back to Role.EXTERNAL for the same reason as `isNOCSelector` above: `companies` starts as
// `[]` for every session, not only one with no companies, so a not-yet-loaded session must read as
// the least-privileged role rather than `undefined` — which a downstream `.includes()` or numeric
// comparison could otherwise treat as an open gate.
export const roleSelector = (state: RootState): number =>
  state.user.companies[0]?.role ?? Role.EXTERNAL;
export const trueRoleSelector = (state: RootState) => state.user.trueRole;
export const sendingEmailSelector = (state: RootState) => state.user.email;
export const startTimeSelector = (state: RootState) => state.user.startTime;
export const userFetchedSelector = (state: RootState) => state.user.userFetched;
export const userExperimentsSelector = (state: RootState) => state.user.experiments;

// How /me answered the boot check. /me is the first authenticated call, so the API Gateway
// authorizer (bpm-lambdas jobs/authorize.js) answers it before any app code:
//  - 403: the token is valid but this identity has no access (no user_access row). Definitive: no
//    refresh can change it, and retrying it is what used to loop the user back to the login page.
//  - 401: the token was rejected (missing, invalid or expired). A refresh may heal it.
//  - anything else (5xx, network, a refresh that couldn't answer): we couldn't check. Never a deny.
const isNotEntitled = (e: unknown): boolean => e instanceof FetchError && e.status === 403;
const isSessionRejected = (e: unknown): boolean =>
  e instanceof NotSignedInError || (e instanceof FetchError && e.status === 401);

// The signed-in email, for the "not entitled" notice. Read before sign-out clears memory.
const signedInEmail = async (): Promise<string | null> => {
  try {
    return decodeJwtEmail((await getTokens()).idToken);
  } catch {
    return null;
  }
};

// Fetches /me and returns the user info, de-duplicating companies. Throws on any failure.
// skipAuthRecovery: true because this specific call is useIsAuthed's own boot check, which
// already implements its own refresh+retry+redirect flow below (isRecoverableMeError et al.) —
// without the flag, BpmLambdaFetch's generic recovery (DEV-24818) would double-handle it.
const fetchMe = async (): Promise<UserAccount> => {
  const userRes = await UsersLambdaFetch('/me', { retries: 0, skipAuthRecovery: true });
  const userInfo = await awaitJSON<UserAccount>(userRes);
  // Prevent duplicate deeply equal values in companies
  userInfo.companies = userInfo.companies.filter(
    (comp, i, arr) => arr.findIndex(c => c.company === comp.company && c.role === comp.role) === i
  );
  return userInfo;
};

// Wrapped so the react-hooks purity rule doesn't flag the intentional wall-clock reads below.
const getNow = () => Date.now();

export const useIsAuthed = () => {
  const dispatch = useDispatch<AppDispatch>();
  const isAuthed = useSelector(isAuthedSelector);
  const authUnavailable = useSelector(authUnavailableSelector);

  // Boot checks are numbered. Only the newest check may change state: a retry supersedes a stalled
  // one, and the stalled one's late failure (or 401) must not override the retry's result.
  const checkSeq = useRef(0);
  // The sequence number of the boot check that is running right now, or null.
  const inFlightSeq = useRef<number | null>(null);
  // Set while the retry view is up, so the next boot-effect run knows it was triggered by a retry.
  const retryRequested = useRef(false);
  const isAuthedRef = useRef(isAuthed);

  // When auth resolution started (null once resolved, or while the retry view is up: waiting for
  // the user to retry is not a stalled resolution). Recomputed on retry, so a retry gets a fresh
  // PAGE_TIMEOUT instead of inheriting the time spent showing the retry view.
  const nilIsAuthedTime = useMemo(
    () => (R.isNil(isAuthed) && !authUnavailable ? getNow() : null),
    [isAuthed, authUnavailable]
  );
  const [now, setNow] = useState(getNow());

  const endingSession = useRef(false);

  // Ends the session for real and shows the Login view: signOutAWS revokes the refresh token and
  // clears the HttpOnly __Host-rt cookie (POST /auth/logout), which clearAuthCookies() alone can't.
  // Without that, every reload restored the same session and bounced again. Only a definitive
  // answer from the boot check (a /me 403, or a 401 that survived a refresh) gets here; a stall or
  // an outage shows the retry view instead. A federated sign-out navigates to the IdP's logout URL
  // and comes back through a fresh load, so the page is left as it is rather than flashing the
  // login view first.
  // `denied` carries the "not entitled" notice to the login page (see access-denied.ts).
  const endSessionAndShowLogin = useCallback(
    async (reason: string, denied?: { email: string | null }) => {
      if (endingSession.current) return;
      endingSession.current = true;
      datadogRum.addError(new Error(reason));
      const navigatingAway = await signOutAWS();
      // After signOutAWS, which clears sessionStorage.
      if (denied) recordAccessDenied(denied.email);
      if (!navigatingAway) {
        endingSession.current = false;
        dispatch({ type: SET_IS_AUTHED, isAuthed: false });
      }
    },
    [dispatch]
  );

  // The session check couldn't get an answer (the refresh endpoint or /me failed, or the check
  // stalled), so we don't know whether a session exists. Never reload and never clear anything:
  // show the retry view and keep the session (its HttpOnly refresh cookie is untouched) so a later
  // retry can pick it straight back up. Doesn't supersede the running check: if that one still
  // answers, its result clears the retry view.
  const markAuthUnavailable = useCallback(
    (e: unknown, context = 'auth_unavailable') => {
      reportError(e, { operation: 'checkAuth', extra: { context } });
      dispatch({ type: SET_AUTH_UNAVAILABLE, authUnavailable: true });
    },
    [dispatch]
  );

  // Final arbiter for a /me failure: a 403 straight away, anything else after one token refresh +
  // retry (see the boot effect below). `isCurrent` is false once a retry has superseded the check.
  const handleMeFailure = useCallback(
    async (e: unknown, isCurrent: () => boolean) => {
      console.error(e);
      if (isNotEntitled(e)) {
        const email = await signedInEmail();
        if (!isCurrent()) return;
        await endSessionAndShowLogin('/me returned 403 (not entitled); signing out', { email });
        return;
      }
      if (isSessionRejected(e)) {
        await endSessionAndShowLogin('/me rejected the session after refresh+retry; signing out');
        return;
      }
      markAuthUnavailable(e);
    },
    [endSessionAndShowLogin, markAuthUnavailable]
  );

  // A refresh that comes back 401 after boot means the session is over: swap to the login view,
  // remembering the page so sign-in returns to it. Ignored while a boot check or a sign-out is
  // running (the boot check's own /me retry can trigger a refresh 401, and that path already ends
  // the session itself) and before the app is signed in.
  useEffect(
    () =>
      onSignedOut(reason => {
        if (reason !== 'session_ended') return;
        if (isAuthedRef.current !== true) return;
        if (inFlightSeq.current !== null || endingSession.current) return;
        captureIntendedDestination();
        dispatch({ type: SET_IS_AUTHED, isAuthed: false });
      }),
    [dispatch]
  );

  useEffect(() => {
    isAuthedRef.current = isAuthed;
  }, [isAuthed]);

  useEffect(() => {
    // The URL carries ?code=&state= params: App holds its loading view for this load and the boot
    // check never runs, so there's no stalled resolution to time out.
    if (oauthRedirectInProgress) return;
    // The retry view is up: this is not a stalled resolution, the user is deciding when to retry.
    if (authUnavailable) return;
    // In local dev the backend lambdas run on localhost and can be slow to cold-start, so
    // the auth check + /me routinely takes longer than PAGE_TIMEOUT — which would spuriously log
    // the user out even though auth is fine (especially across frequent HMR reloads). Skip the
    // timeout entirely when served from localhost (the same signal fetch-utils uses to route to
    // local lambdas). Production is unaffected.
    if (window.location.hostname === 'localhost') {
      return;
    }
    // A sign-out in progress already decides what the user sees next.
    if (endingSession.current) return;
    // If auth resolution has been stuck for over 10 seconds, show the retry view. A stall says
    // nothing about the session, so nothing is signed out.
    if (nilIsAuthedTime !== null) {
      if (now - nilIsAuthedTime > PAGE_TIMEOUT) {
        markAuthUnavailable(
          new Error('Credential verification timed out; showing retry view'),
          'boot_timeout'
        );
      } else {
        setTimeout(() => {
          setNow(getNow());
        }, 1000);
      }
    }
  }, [authUnavailable, markAuthUnavailable, nilIsAuthedTime, now]);

  useEffect(() => {
    // The URL carries ?code=&state= params: skip the boot check for this load.
    if (oauthRedirectInProgress) return;
    // Waiting for the user to retry: don't hammer the refresh endpoint from here.
    if (authUnavailable) {
      retryRequested.current = true;
      return;
    }
    // If this is not null, we already know the answer.
    if (!R.isNil(isAuthed)) return;
    const isRetry = retryRequested.current;
    retryRequested.current = false;
    // A check is already running. A retry replaces it instead (the old one's results are dropped).
    if (inFlightSeq.current !== null && !isRetry) return;

    checkSeq.current += 1;
    const seq = checkSeq.current;
    inFlightSeq.current = seq;
    const isCurrent = () => checkSeq.current === seq;

    (async () => {
      try {
        let authCheck: boolean;
        try {
          authCheck = await checkAuth();
        } catch (e) {
          if (!isCurrent()) return;
          if (e instanceof NotSignedInError) {
            authCheck = false;
          } else {
            // RefreshUnavailableError (or anything unexpected): no answer, so no state change.
            markAuthUnavailable(e);
            return;
          }
        }
        if (!isCurrent()) return;

        // If we're authenticated, call the "me" endpoint. Otherwise, send to the login page
        if (!authCheck) {
          dispatch({
            type: SET_IS_AUTHED,
            isAuthed: false
          });
          return;
        }
        try {
          let userInfo: UserAccount;
          try {
            userInfo = await fetchMe();
          } catch (e) {
            // A 403 is final. Anything else gets one forced token refresh and one retry of /me,
            // which heals a stale or expired token in place and rides out a single blip.
            if (isNotEntitled(e) || !isCurrent()) {
              throw e;
            }
            datadogRum.addAction('/me failed; refreshing token and retrying once');
            await refreshCognitoToken();
            if (!isCurrent()) return;
            userInfo = await fetchMe();
          }
          if (!isCurrent()) return;
          dispatch({
            type: SET_USER_INFO,
            userInfo
          });
          datadogRum.setUser({ email: userInfo.email, user: userInfo.email });
          followReturnToAfterBoot();
        } catch (e) {
          if (!isCurrent()) return;
          await handleMeFailure(e, isCurrent);
        }
      } finally {
        if (inFlightSeq.current === seq) inFlightSeq.current = null;
      }
    })();
  }, [isAuthed, authUnavailable, dispatch, handleMeFailure, markAuthUnavailable]);

  return isAuthed;
};

// "Try again" for the boot retry view: clears the refresh backoff (the user asked, so the next
// refresh must reach the server) and the unavailable flag, which re-runs the session check.
export const useRetryAuth = () => {
  const dispatch = useDispatch<AppDispatch>();
  return useCallback(() => {
    resetRefreshBackoff();
    dispatch({ type: SET_AUTH_UNAVAILABLE, authUnavailable: false });
  }, [dispatch]);
};

// Logout entry point for UI. Keeps the current page on screen while the sign-out request completes
// (a brief "hang", no spinner and no premature switch), then flips isAuthed to false so the app
// swaps to the login view in React — no full-page reload, so no logout "blink".
export const useLogout = () => {
  const dispatch = useDispatch<AppDispatch>();
  return useCallback(() => {
    (async () => {
      await signOutAWS();
      dispatch({ type: SET_IS_AUTHED, isAuthed: false });
    })();
  }, [dispatch]);
};

const fetchLatestCommits = async (retries = 10) => {
  const latestCommitRes = await UsersLambdaFetch('/latest_commit', {
    retries,
    params: {
      repo: 'bpm-frontend'
    }
  });
  const frontendHash = await awaitJSON<GetLatestCommitResponse>(latestCommitRes);
  return {
    frontend: frontendHash.hash
  };
};

export const useVersionCheck = () => {
  const dispatch = useDispatch<AppDispatch>();
  const hasFetched = useRef(false);
  const isAuthed = useSelector(isAuthedSelector);

  const makeVersionCheckInterval = useCallback(
    (hashes: { frontend: string }) => {
      let newFrontend = false;
      const int = setInterval(async () => {
        try {
          // `finally` still refreshes credentials for a hidden tab.
          if (document.hidden) return;
          // The next tick is the retry.
          const latestHashes = await fetchLatestCommits(1);
          if (!newFrontend && hashes.frontend !== latestHashes.frontend) {
            console.warn('Newer version of the app available');
            dispatch({
              type: SET_UP_TO_DATE,
              appUpToDate: false
            });
            newFrontend = true;
          }
          if (newFrontend) {
            clearInterval(int);
          }
        } catch {
          console.error('Failed to fetch latest commits after several retries.');
        } finally {
          datadogRum.addAction('Refreshing credentials during version check interval', {
            category: 'auth'
          });
          await initPeriodicRefresh();
        }
      }, VERSION_CHECK_INTERVAL);
      return int;
    },
    [dispatch]
  );

  useEffect(() => {
    // The version-check fetch has no test value (no spec exercises the update-available UI)
    // and is one of the earliest things the app does after every cy.visit — exactly the class
    // of request most prone to escaping cy.intercept's proxy attachment right after a fresh
    // page navigation, surfacing as repeated ECONNREFUSED noise in the Vite proxy log. Skipping
    // it under Cypress (same pattern as src/utils/auth/auth.ts's window.Cypress check) removes
    // the request at the source instead of relying on interception timing to catch it.
    if (window.Cypress) return;
    if (isAuthed && !hasFetched.current) {
      hasFetched.current = true;
      (async () => {
        try {
          const hashes = await fetchLatestCommits();

          datadogRum.setGlobalContextProperty('commitHash', hashes.frontend);
          if (import.meta.env.PROD) {
            makeVersionCheckInterval(hashes);
          }
        } catch (e) {
          const message = `Failed to get latest commit hash on first try. Error: ${e.message}`;
          console.error(message);
          reportError(message, { operation: 'fetchLatestCommits' });
        }
      })();
    }
  }, [isAuthed, makeVersionCheckInterval]);
};

export const useUpToDate = () => {
  const dispatch = useDispatch<AppDispatch>();
  const appUpToDate = useSelector(appUpToDateSelector);
  const dismissed = useSelector(appUpToDateDismissedSelector);

  const dismiss = useCallback(() => {
    dispatch({
      type: SET_UP_TO_DATE_DISMISSED,
      appUpToDateDismissed: true
    });
  }, [dispatch]);

  return {
    appUpToDate: dismissed || appUpToDate,
    dismiss
  };
};

const setIsAuthed = (state: UserState, { isAuthed }: { isAuthed: boolean }): UserState => ({
  ...state,
  isAuthed,
  authUnavailable: false
});

const setAuthUnavailable = (
  state: UserState,
  { authUnavailable }: { authUnavailable: boolean }
): UserState => ({
  ...state,
  authUnavailable
});

const setUserInfo = (state: UserState, { userInfo }: { userInfo: UserAccount }): UserState => ({
  ...state,
  ...userInfo,
  isAuthed: true,
  authUnavailable: false,
  userFetched: true
});

const setUpToDate = (state: UserState, { appUpToDate }: { appUpToDate: boolean }): UserState => ({
  ...state,
  appUpToDate
});

const setUpToDateDismiss = (
  state: UserState,
  { appUpToDateDismissed }: { appUpToDateDismissed: boolean }
): UserState => ({
  ...state,
  appUpToDateDismissed
});

export default makeReducer<UserState>(
  {
    agencies: [],
    companies: [],
    email: '',
    experiments: [],
    fullName: '',
    isAdmin: false,
    isInternal: false,
    role: Role.EXTERNAL,
    send_linear_buys: false,
    simplifi_title: null,
    trueRole: Role.EXTERNAL,
    userFetched: false,
    appUpToDate: true,
    appUpToDateDismissed: false,
    startTime: Date.now()
  },
  {
    [SET_IS_AUTHED]: setIsAuthed,
    [SET_AUTH_UNAVAILABLE]: setAuthUnavailable,
    [SET_USER_INFO]: setUserInfo,
    [SET_UP_TO_DATE]: setUpToDate,
    [SET_UP_TO_DATE_DISMISSED]: setUpToDateDismiss
  }
);
